import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { aiReadinessService } from '@/services/aiReadinessService';
import { eventBus } from '@/services/eventBus';
import type {
  Character,
  CharacterImportance,
  CharacterRole,
  CharacterArchetype,
  GenderPronouns,
  NarrativePerspective,
  CharacterWithReadiness
} from '@/types';

export interface CreateCharacterInput {
  name: string;
  role: CharacterRole;
  importance: CharacterImportance;
  perspective: NarrativePerspective;
  gender: GenderPronouns;
  archetype?: CharacterArchetype;
  age?: number;
  tags?: string[];
  color?: string;
}

export interface UpdateCharacterInput {
  characterId: string;
  payload: Partial<Character>;
}

type CharacterMap = Record<string, Character[]>;
type ReadinessMap = Record<string, Record<string, CharacterWithReadiness['readiness'] | undefined>>;

interface CharacterStoragePayload {
  version: string;
  lastUpdated: string;
  characters: Character[];
}

const STORAGE_PREFIX = 'app_characters';
const STORAGE_VERSION = '1.0.0';

const storageKey = (manuscriptId: string): string => `${STORAGE_PREFIX}:${manuscriptId}`;

const generateId = (prefix: string): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const randomSuffix = Math.random().toString(36).slice(2, 10);
  return `${prefix}-${randomSuffix}`;
};

const cloneCharacter = (character: Character): Character => ({
  ...character,
  tags: [...character.tags],
  uiState: {
    ...character.uiState,
    expandedFields: [...character.uiState.expandedFields]
  },
  appearances: {
    ...character.appearances,
    chapterIds: [...character.appearances.chapterIds],
    mentionedInChapterIds: [...character.appearances.mentionedInChapterIds]
  },
  summaryMetadata: { ...character.summaryMetadata },
  setupMetadata: { ...character.setupMetadata },
  skills: {
    ...character.skills,
    expertAt: [...character.skills.expertAt],
    proficientAt: [...character.skills.proficientAt],
    basicKnowledge: character.skills.basicKnowledge ? [...character.skills.basicKnowledge] : undefined,
    cannotDo: [...character.skills.cannotDo],
    learning: character.skills.learning ? [...character.skills.learning] : undefined,
    specialAbilities: character.skills.specialAbilities ? [...character.skills.specialAbilities] : undefined,
    weaknesses: character.skills.weaknesses ? [...character.skills.weaknesses] : undefined
  },
  emotionalPatterns: { ...character.emotionalPatterns },
  voice: {
    ...character.voice,
    commonPhrases: [...character.voice.commonPhrases],
    verbalTics: [...character.voice.verbalTics]
  },
  narrativeDetails: { ...character.narrativeDetails }
});

export const useCharactersStore = defineStore('characters', () => {
  const charactersByManuscript = ref<CharacterMap>({});
  const readinessByManuscript = ref<ReadinessMap>({});
  const cache = ref<Map<string, Character[]>>(new Map());

  const activeManuscriptId = ref<string | null>(null);
  const isLoading = ref(false);
  const lastError = ref<string | null>(null);

  const ensureManuscriptState = (manuscriptId: string): void => {
    if (!charactersByManuscript.value[manuscriptId]) {
      charactersByManuscript.value[manuscriptId] = [];
    }

    if (!readinessByManuscript.value[manuscriptId]) {
      readinessByManuscript.value[manuscriptId] = {};
    }
  };

  const invalidateCache = (manuscriptId?: string): void => {
    if (!manuscriptId) {
      cache.value.clear();
      return;
    }

    const keys = Array.from(cache.value.keys()).filter((key) => key.startsWith(`${manuscriptId}:`));
    keys.forEach((key) => cache.value.delete(key));
  };

  const cacheCharacters = (manuscriptId: string, characters: Character[]): void => {
    cache.value.set(`${manuscriptId}:active`, characters.map(cloneCharacter));
  };

  const loadFromStorage = (manuscriptId: string): void => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const raw = window.localStorage.getItem(storageKey(manuscriptId));
      if (!raw) {
        ensureManuscriptState(manuscriptId);
        return;
      }

      const payload = JSON.parse(raw) as CharacterStoragePayload;
      if (payload.version !== STORAGE_VERSION || !Array.isArray(payload.characters)) {
        ensureManuscriptState(manuscriptId);
        return;
      }

      charactersByManuscript.value = {
        ...charactersByManuscript.value,
        [manuscriptId]: payload.characters.map((character) => cloneCharacter(character))
      };
      invalidateCache(manuscriptId);
    } catch (error) {
      console.error('[useCharactersStore] Failed to load characters from storage', { manuscriptId, error });
      ensureManuscriptState(manuscriptId);
    }
  };

  const persistToStorage = (manuscriptId: string): void => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const payload: CharacterStoragePayload = {
        version: STORAGE_VERSION,
        lastUpdated: new Date().toISOString(),
        characters: (charactersByManuscript.value[manuscriptId] ?? []).map((character) => cloneCharacter(character))
      };

      window.localStorage.setItem(storageKey(manuscriptId), JSON.stringify(payload));
    } catch (error) {
      console.error('[useCharactersStore] Failed to persist characters to storage', { manuscriptId, error });
    }
  };

  const activeCharacters = computed<Character[]>(() => {
    if (!activeManuscriptId.value) {
      return [];
    }

    const manuscriptId = activeManuscriptId.value;
    const cacheKey = `${manuscriptId}:active`;
    const cached = cache.value.get(cacheKey);
    if (cached) {
      return cached.map((character) => cloneCharacter(character));
    }

    const source = charactersByManuscript.value[manuscriptId] ?? [];
    const clones = source.map((character) => cloneCharacter(character));
    cacheCharacters(manuscriptId, clones);
    return clones.map((character) => cloneCharacter(character));
  });

  const characterCount = computed(() => activeCharacters.value.length);

  const charactersByRole = computed(() => {
    return activeCharacters.value.reduce<Record<CharacterRole, Character[]>>(
      (accumulator, character) => {
        if (!accumulator[character.role]) {
          accumulator[character.role] = [];
        }

        accumulator[character.role].push(character);
        return accumulator;
      },
      {
        protagonist: [],
        antagonist: [],
        supporting: [],
        mentor: [],
        'love-interest': [],
        sidekick: [],
        foil: [],
        minor: [],
        other: []
      }
    );
  });

  const setActiveManuscript = (manuscriptId: string | null): void => {
    activeManuscriptId.value = manuscriptId;
    if (!manuscriptId) {
      return;
    }

    ensureManuscriptState(manuscriptId);
    loadFromStorage(manuscriptId);
  };

  const getCharactersForManuscript = (manuscriptId: string): Character[] => {
    ensureManuscriptState(manuscriptId);
    return charactersByManuscript.value[manuscriptId] ?? [];
  };

  const nextCharacterNumber = (collection: Character[]): number => {
    if (collection.length === 0) {
      return 1;
    }

    const highest = collection.reduce((max, item) => (item.number > max ? item.number : max), 0);
    return highest + 1;
  };

  const createCharacter = async (input: CreateCharacterInput): Promise<Character> => {
    if (!activeManuscriptId.value) {
      throw new Error('Cannot create a character without an active manuscript.');
    }

    isLoading.value = true;
    lastError.value = null;

    try {
      const manuscriptId = activeManuscriptId.value;
      ensureManuscriptState(manuscriptId);
      const currentCollection = [...getCharactersForManuscript(manuscriptId)];
      const now = new Date().toISOString();

      const character: Character = {
        id: generateId('character'),
        manuscriptId,
        number: nextCharacterNumber(currentCollection),
        name: input.name,
        age: input.age,
        role: input.role,
        archetype: input.archetype,
        gender: input.gender,
        perspective: input.perspective,
        importance: input.importance,
        characterSummary: '',
        summaryMetadata: {
          lastUpdated: now,
          source: 'manual',
          needsRegeneration: true
        },
        narrativeDetails: {},
        voice: {
          speakingStyle: '',
          voiceDescription: '',
          vocabularyLevel: 'conversational',
          sentenceStructure: 'conversational',
          commonPhrases: [],
          verbalTics: [],
          exampleDialogue: '',
          accentOrDialect: undefined
        },
        skills: {
          expertAt: [],
          proficientAt: [],
          basicKnowledge: undefined,
          cannotDo: [],
          learning: undefined,
          specialAbilities: undefined,
          weaknesses: undefined
        },
        emotionalPatterns: {
          whenAngry: '',
          whenAfraid: '',
          whenHappy: '',
          whenSad: '',
          underStress: '',
          whenLying: '',
          whenComfortable: ''
        },
        appearances: {
          chapterIds: [],
          mentionedInChapterIds: [],
          lastAppearance: undefined,
          firstAppearance: undefined
        },
        uiState: {
          lastActiveTab: 'overview',
          expandedFields: [],
          isExpanded: false
        },
        setupMetadata: {
          setupComplete: false,
          setupLevel: 'quick',
          completedAt: undefined
        },
        createdAt: now,
        updatedAt: now,
        tags: input.tags ?? [],
        notes: '',
        color: input.color
      };

      const updatedCollection = [...currentCollection, character];
      charactersByManuscript.value = {
        ...charactersByManuscript.value,
        [manuscriptId]: updatedCollection
      };

      invalidateCache(manuscriptId);
      persistToStorage(manuscriptId);

      eventBus.emit('character:changed', {
        manuscriptId,
        characterId: character.id,
        action: 'created'
      });

      return cloneCharacter(character);
    } catch (error) {
      console.error('[useCharactersStore] Failed to create character', { error, input });
      lastError.value = 'Unable to create character.';
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const updateCharacter = async ({ characterId, payload }: UpdateCharacterInput): Promise<Character> => {
    if (!activeManuscriptId.value) {
      throw new Error('Cannot update a character without an active manuscript.');
    }

    const manuscriptId = activeManuscriptId.value;
    ensureManuscriptState(manuscriptId);
    const collection = [...getCharactersForManuscript(manuscriptId)];
    const index = collection.findIndex((character) => character.id === characterId);

    if (index === -1) {
      throw new Error('Character not found.');
    }

    isLoading.value = true;
    lastError.value = null;

    try {
      const existing = collection[index];
      const updated: Character = {
        ...existing,
        ...payload,
        narrativeDetails: payload.narrativeDetails ? { ...payload.narrativeDetails } : existing.narrativeDetails,
        voice: payload.voice ? { ...payload.voice } : existing.voice,
        skills: payload.skills ? { ...payload.skills } : existing.skills,
        emotionalPatterns: payload.emotionalPatterns ? { ...payload.emotionalPatterns } : existing.emotionalPatterns,
        appearances: payload.appearances ? { ...payload.appearances } : existing.appearances,
        uiState: payload.uiState ? { ...payload.uiState } : existing.uiState,
        setupMetadata: payload.setupMetadata ? { ...payload.setupMetadata } : existing.setupMetadata,
        tags: payload.tags ? [...payload.tags] : existing.tags,
        updatedAt: new Date().toISOString()
      };

      collection[index] = updated;
      charactersByManuscript.value = {
        ...charactersByManuscript.value,
        [manuscriptId]: collection
      };

      invalidateCache(manuscriptId);
      persistToStorage(manuscriptId);
      aiReadinessService.invalidate(manuscriptId, characterId);

      eventBus.emit('character:changed', {
        manuscriptId,
        characterId,
        action: 'updated'
      });

      return cloneCharacter(updated);
    } catch (error) {
      console.error('[useCharactersStore] Failed to update character', { error, characterId, payload });
      lastError.value = 'Unable to update character.';
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteCharacter = async (characterId: string): Promise<void> => {
    if (!activeManuscriptId.value) {
      throw new Error('Cannot delete a character without an active manuscript.');
    }

    const manuscriptId = activeManuscriptId.value;
    ensureManuscriptState(manuscriptId);
    const collection = getCharactersForManuscript(manuscriptId);
    const exists = collection.some((character) => character.id === characterId);

    if (!exists) {
      throw new Error('Character not found.');
    }

    const updatedCollection = collection.filter((character) => character.id !== characterId);
    charactersByManuscript.value = {
      ...charactersByManuscript.value,
      [manuscriptId]: updatedCollection
    };

    if (readinessByManuscript.value[manuscriptId]) {
      const { [characterId]: removed, ...remaining } = readinessByManuscript.value[manuscriptId];
      readinessByManuscript.value = {
        ...readinessByManuscript.value,
        [manuscriptId]: remaining
      };
    }

    invalidateCache(manuscriptId);
    persistToStorage(manuscriptId);
    aiReadinessService.invalidate(manuscriptId, characterId);

    eventBus.emit('character:changed', {
      manuscriptId,
      characterId,
      action: 'deleted'
    });
  };

  const replaceCharacters = (manuscriptId: string, collection: Character[]): void => {
    ensureManuscriptState(manuscriptId);
    charactersByManuscript.value = {
      ...charactersByManuscript.value,
      [manuscriptId]: collection.map((character) => cloneCharacter(character))
    };

    invalidateCache(manuscriptId);
    persistToStorage(manuscriptId);
    aiReadinessService.invalidate(manuscriptId);
  };

  const clearManuscript = (manuscriptId: string): void => {
    delete charactersByManuscript.value[manuscriptId];
    delete readinessByManuscript.value[manuscriptId];
    invalidateCache(manuscriptId);
    aiReadinessService.invalidate(manuscriptId);

    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(storageKey(manuscriptId));
    }

    if (activeManuscriptId.value === manuscriptId) {
      activeManuscriptId.value = null;
    }
  };

  const resetStore = (): void => {
    charactersByManuscript.value = {};
    readinessByManuscript.value = {};
    cache.value.clear();
    activeManuscriptId.value = null;
    isLoading.value = false;
    lastError.value = null;
  };

  const getCharacterById = (characterId: string): Character | null => {
    return activeCharacters.value.find((item) => item.id === characterId) ?? null;
  };

  const setReadiness = (characterId: string, readiness: CharacterWithReadiness['readiness']): void => {
    if (!activeManuscriptId.value) {
      throw new Error('Cannot set readiness without an active manuscript.');
    }

    const manuscriptId = activeManuscriptId.value;
    ensureManuscriptState(manuscriptId);
    const readinessState = readinessByManuscript.value[manuscriptId] ?? {};

    readinessByManuscript.value = {
      ...readinessByManuscript.value,
      [manuscriptId]: {
        ...readinessState,
        [characterId]: readiness
      }
    };

    eventBus.emit('aiReadiness:changed', {
      manuscriptId,
      system: 'characters',
      entityId: characterId,
      readiness: readiness?.overall ?? null
    });
  };

  const getReadinessForManuscript = (
    manuscriptId: string
  ): Record<string, CharacterWithReadiness['readiness'] | undefined> => {
    ensureManuscriptState(manuscriptId);
    const readiness = readinessByManuscript.value[manuscriptId] ?? {};
    return { ...readiness };
  };

  const getAIReadiness = (characterId: string): CharacterWithReadiness['readiness'] => {
    if (!activeManuscriptId.value) {
      return undefined;
    }

    return readinessByManuscript.value[activeManuscriptId.value]?.[characterId];
  };

  return {
    activeCharacters,
    characterCount,
    charactersByRole,
    isLoading,
    lastError,
    setActiveManuscript,
    getCharactersForManuscript,
    createCharacter,
    updateCharacter,
    deleteCharacter,
    replaceCharacters,
    clearManuscript,
    resetStore,
    getCharacterById,
    setReadiness,
    getAIReadiness,
    getReadinessForManuscript
  };
});
